import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Layout, Star, Phone, Image as ImageIcon } from "lucide-react";
import { GalleryAdmin, FaqAdmin } from "./catalog";

const SECTIONS = {
  hero: [
    { key: "title", type: "bilingual", label_ar: "العنوان الرئيسي", label_en: "Hero title" },
    { key: "subtitle", type: "bilingual", label_ar: "النص الفرعي", label_en: "Subtitle" },
    { key: "badge", type: "bilingual", label_ar: "الشارة العلوية", label_en: "Badge" },
    { key: "image", type: "image", label_ar: "صورة الخلفية", label_en: "Background image" },
    { key: "cta_primary", type: "bilingual", label_ar: "زر الحجز", label_en: "Primary CTA" },
    { key: "cta_secondary", type: "bilingual", label_ar: "الزر الثانوي", label_en: "Secondary CTA" },
  ],
  marketing: [
    { key: "services_title", type: "bilingual", label_ar: "عنوان قسم الخدمات", label_en: "Services title" },
    { key: "services_sub", type: "bilingual", label_ar: "وصف قسم الخدمات", label_en: "Services subtitle" },
    { key: "gallery_title", type: "bilingual", label_ar: "عنوان المعرض", label_en: "Gallery title" },
    { key: "gallery_sub", type: "bilingual", label_ar: "وصف المعرض", label_en: "Gallery subtitle" },
    { key: "cta_title", type: "bilingual", label_ar: "عنوان الدعوة النهائية", label_en: "Final CTA title" },
    { key: "banner", type: "bilingual", label_ar: "شريط العرض الترويجي", label_en: "Promo banner" },
    { key: "banner_enabled", type: "checkbox", label_ar: "تفعيل الشريط الترويجي", label_en: "Enable promo banner" },
  ],
  contact: [
    { key: "phone", type: "text", label_ar: "الهاتف", label_en: "Phone" },
    { key: "email", type: "text", label_ar: "البريد", label_en: "Email" },
    { key: "whatsapp", type: "text", label_ar: "واتساب", label_en: "WhatsApp" },
    { key: "instagram", type: "text", label_ar: "انستغرام", label_en: "Instagram" },
    { key: "tiktok", type: "text", label_ar: "تيك توك", label_en: "TikTok" },
    { key: "snapchat", type: "text", label_ar: "سناب شات", label_en: "Snapchat" },
    { key: "address", type: "bilingual", label_ar: "العنوان", label_en: "Address" },
  ],
};

export default function ContentEditor() {
  const { t, isAr } = useI18n();
  const [tab, setTab] = useState("hero");
  const [content, setContent] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/content").then((r) => setContent(r.data)).catch(() => {}); }, []);

  const data = content[tab] || {};
  const setVal = (k, v) => setContent((p) => ({ ...p, [tab]: { ...(p[tab] || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/content/${tab}`, content[tab] || {}); toast.success(isAr ? "تم حفظ المحتوى" : "Content saved"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const tabs = [["hero", Layout, isAr ? "الواجهة الرئيسية" : "Hero"], ["marketing", Star, isAr ? "التسويق" : "Marketing"], ["contact", Phone, isAr ? "التواصل" : "Contact"], ["gallery", ImageIcon, isAr ? "المعرض" : "Gallery"], ["faq", Layout, isAr ? "الأسئلة" : "FAQ"]];
  const base = "mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-primary focus:outline-none";
  const isCms = !!SECTIONS[tab];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-xl font-bold text-slate-900">{isAr ? "التحكم بالواجهة والمحتوى" : "Homepage & Content Control"}</h1>
          <p className="text-sm text-slate-500">{isAr ? "عدّل محتوى الصفحة الرئيسية بالكامل بالعربية والإنجليزية" : "Fully edit the homepage content in Arabic & English"}</p></div>
        {isCms && <button onClick={save} disabled={busy} data-testid="content-save-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" /> {busy ? t("common.loading") : t("common.save")}</button>}
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto no-scrollbar border-b border-border">
        {tabs.map(([k, Icon, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`content-tab-${k}`} className={`flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-medium ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-500"}`}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>

      {tab === "gallery" && <div className="mt-6"><GalleryAdmin /></div>}
      {tab === "faq" && <div className="mt-6"><FaqAdmin /></div>}

      {isCms && <div className="mt-6 max-w-2xl space-y-5">
        {SECTIONS[tab].map((f) => {
          const label = isAr ? f.label_ar : f.label_en;
          if (f.type === "bilingual") return (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-700">{label}</label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input dir="rtl" placeholder="عربي" value={data[`${f.key}_ar`] || ""} onChange={(e) => setVal(`${f.key}_ar`, e.target.value)} data-testid={`content-${f.key}-ar`} className={base + " mt-0"} />
                <input dir="ltr" placeholder="English" value={data[`${f.key}_en`] || ""} onChange={(e) => setVal(`${f.key}_en`, e.target.value)} data-testid={`content-${f.key}-en`} className={base + " mt-0"} />
              </div>
            </div>
          );
          if (f.type === "checkbox") return (
            <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!data[f.key]} onChange={(e) => setVal(f.key, e.target.checked)} data-testid={`content-${f.key}`} className="h-4 w-4" /> {label}</label>
          );
          if (f.type === "image") return (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-700">{label}</label>
              <input dir="ltr" value={data[f.key] || ""} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`content-${f.key}`} className={base} placeholder="https://..." />
              {data[f.key] && <img src={data[f.key]} alt="" className="mt-2 h-28 w-full max-w-md rounded-lg object-cover" />}
            </div>
          );
          return (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-700">{label}</label>
              <input dir="ltr" value={data[f.key] || ""} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`content-${f.key}`} className={base} />
            </div>
          );
        })}
      </div>}
    </div>
  );
}
